import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  Optional,
} from "@angular/core";

interface PasswordToggleTarget {
  control?: () => HTMLInputElement | undefined;
  toggleInputType?: () => void;
}

@Directive({
  selector: "[appPasswordTouchToggle]",
  standalone: true,
})
export class NxPasswordTouchToggleDirective implements OnDestroy {
  @Input("appPasswordTouchToggle") public target?: PasswordToggleTarget;

  private readonly mobileRevealDurationMs = 500;
  private readonly hostElement?: HTMLElement;
  private isMobileRevealActive = false;
  private mobileRevealTimeoutId?: ReturnType<typeof setTimeout>;

  private readonly nativeClickListener = (event: MouseEvent): void => {
    this.onClick(event);
  };

  constructor(@Optional() elementRef?: ElementRef<HTMLElement>) {
    this.hostElement = elementRef?.nativeElement;

    // Capture before nx-password-toggle's own click handler can persist visibility.
    this.hostElement?.addEventListener?.(
      "click",
      this.nativeClickListener,
      true,
    );
  }

  public ngOnDestroy(): void {
    this.hostElement?.removeEventListener?.(
      "click",
      this.nativeClickListener,
      true,
    );
    this.clearMobileRevealTimeout();
  }

  @HostListener("mousedown", ["$event"])
  protected onMouseDown(event: MouseEvent): void {
    event.preventDefault();
    if (this.isMobileRevealActive) {
      return;
    }
    this.show();
  }

  @HostListener("document:mouseup")
  protected onMouseUp(): void {
    this.hideUnlessMobileRevealActive();
  }

  @HostListener("mouseleave")
  protected onMouseLeave(): void {
    this.hideUnlessMobileRevealActive();
  }

  private hideUnlessMobileRevealActive(): void {
    if (this.isMobileRevealActive) {
      return;
    }
    this.hide();
  }

  @HostListener("touchstart", ["$event"])
  protected onTouchStart(event: TouchEvent): void {
    event.preventDefault();
    this.show();
    this.isMobileRevealActive = true;
    this.clearMobileRevealTimeout();
    this.mobileRevealTimeoutId = setTimeout(() => {
      this.isMobileRevealActive = false;
      this.mobileRevealTimeoutId = undefined;
      this.hide();
    }, this.mobileRevealDurationMs);
  }

  protected onClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.isMobileRevealActive) {
      return;
    }
    this.hide();
  }

  private clearMobileRevealTimeout(): void {
    if (!this.mobileRevealTimeoutId) {
      return;
    }
    clearTimeout(this.mobileRevealTimeoutId);
    this.mobileRevealTimeoutId = undefined;
  }

  private show(): void {
    const inputEl = this.target?.control?.();
    if (!inputEl) {
      return;
    }
    if (inputEl.type !== "text") {
      this.target?.toggleInputType?.();
    }
  }

  private hide(): void {
    const inputEl = this.target?.control?.();
    if (inputEl?.type === "text") {
      this.target?.toggleInputType?.();
    }
  }
}
